/**
 * Layouts array.
 */
export type KeyboardLayout = Array<Array<string>>;

/**
 * Alphanumeric keyboard.
 */
export const alphanumericKeyboard: KeyboardLayout = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'Backspace:2'],
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', 'CapsLock:2'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'Spacer', 'Shift:2'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm', 'Spacer:5'],
];

/**
 * Alphanumeric nordic keyboard.
 */
export const alphanumericNordicKeyboard: KeyboardLayout = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'Spacer', 'Backspace:2'],
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', 'å', 'CapsLock:2'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'ö', 'ä', 'Shift:2'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm', 'Spacer:6'],
];

/**
 * Extended keyboard.
 */
export const extendedKeyboard: KeyboardLayout = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'Backspace:2'],
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', 'CapsLock:2'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'Spacer', 'Shift:2'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm', ',', '.', '-', '_', '+'],
  ['Spacer', '@', 'SpaceBar:7', '#', 'Spacer:2'],
];

/**
 * Extended nordic keyboard.
 */
export const extendedNordicKeyboard: KeyboardLayout = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '+', 'Backspace:2'],
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', 'å', 'CapsLock:2'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'ö', 'ä', 'Shift:2'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm', ',', '.', '-', '_', 'Spacer:2'],
  ['Spacer', '@', 'SpaceBar:7', '#', 'Spacer:3'],
];

/**
 * Numeric keyboard.
 */
export const numericKeyboard: KeyboardLayout = [
  ['1', '2', '3', 'Backspace:2'],
  ['4', '5', '6', 'Spacer:2'],
  ['7', '8', '9', 'Spacer:2'],
  ['Spacer', '0', 'Spacer:3'],
];

/**
 * Phone keyboard.
 */
export const phoneKeyboard: KeyboardLayout = [
  ['1', '2', '3', 'Backspace:2'],
  ['4', '5', '6', 'Spacer:2'],
  ['7', '8', '9', 'Spacer:2'],
  ['-', '0', '+', 'Spacer:2'],
];

/**
 * Special keys keyboard.
 */
export const specialKeys: Array<string> = [
  'Enter',
  'Backspace',
  'Escape',
  'CapsLock',
  'SpaceBar',
  'Spacer',
  'Shift',
];

/**
 * Icons keyboard.
 */
export const specialKeyIcons = {
  Enter: 'keyboard_return',
  Backspace: 'backspace',
  Escape: 'close',
  SpaceBar: 'space_bar',
  Shift: 'arrow-round-up',
  CapsLock: 'arrow-round-up'
};

/**
 * Text of special keys.
 */
export const specialKeyTexts = {
  //CapsLock: 'Caps'
};

/**
 * Not disbale special keys.
 */
export const notDisabledSpecialKeys = [
  'Enter',
  'Backspace',
  'Escape',
];

/**
 * Keyboards layouts.
 */
export const keyboardLayouts = {
  alphanumericKeyboard: alphanumericKeyboard,
  alphanumericNordicKeyboard: alphanumericNordicKeyboard,
  extendedKeyboard: extendedKeyboard,
  extendedNordicKeyboard: extendedNordicKeyboard,
  numericKeyboard: numericKeyboard,
  phoneKeyboard: phoneKeyboard
}


/**
 * Return the keyboard from string
 *
 * @export
 * @param {string} layout The key of the layout.
 * @returns
 */
export function getLayout(layout: string){
  return keyboardLayouts[layout] || [];
}

/**
 * Helper function to determine if given key is 'Spacer' or not.
 *
 * @param {string}  key
 * @returns {boolean}
 */
export function isSpacer(key: string): boolean {
  if (key.length > 1) {
    return /^Spacer(:(\d+(\.\d+)?))?$/g.test(key);
  }

  return false;
}

/**
 * Helper function to determine if given key is special or not.
 *
 * @param {string}  key
 * @returns {boolean}
 */
export function isSpecial(key: string): boolean {
  if (key.length > 1) {
    return !!specialKeys.filter(specialKey => {
      const pattern = new RegExp(`^(${specialKey})(:(\\d+(\\.\\d+)?))?$`, 'g');

      return pattern.test(key);
    }).length;
  }

  return false;
}

/**
 * Function to change specified layout to CapsLock layout.
 *
 * @param {KeyboardLayout}  layout
 * @param {boolean}         caps
 * @returns {KeyboardLayout}
 */
export function keyboardCapsLockLayout(layout: KeyboardLayout, caps: boolean): KeyboardLayout {
  return layout.map((row: Array<string>): Array<string> => {
    return row.map((key: string): string => {
      return isSpecial(key) ? key : (caps ? key.toUpperCase() : key.toLowerCase());
    });
  });
}



// WEBPACK FOOTER //
// ./src/modules/utils/ion-virtual-keyboard/layouts.ts